import React, { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-bootstrap/Pagination';
import { LoadingScreen } from '../loading/LoadingScreen';
import { getDeliveryHistory, DeliveryHistory, DeliveryHistoryProductVariation } from '../../utils/getData/DeliveryHistory';

// バックエンドの DELIVERY_HISTORY_PAGE_SIZE と揃える(表示件数の計算にのみ使う)
const PAGE_SIZE = 10;

const getTotalProductVariations = (productVariations: { [productName: string]: DeliveryHistoryProductVariation[] }) => {
  return Object.values(productVariations).reduce((total, variations) => total + variations.length, 0);
};

// ページ数が増えても並びきるように、現在ページの前後2つ分だけ番号を出す
const buildPageNumbers = (page: number, totalPages: number) => {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, Math.max(page + 2, 5));
  const pageNumbers = [];
  for (let i = start; i <= end; i++) {
    pageNumbers.push(i);
  }
  return pageNumbers;
};

export const DeliveryHistoryList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [histories, setHistories] = useState<DeliveryHistory[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getDeliveryHistory(page);
        setHistories(response.results);
        setTotalPages(response.total_pages);
        setTotalCount(response.total_count);
        // 範囲外を指定した場合はバックエンドが丸めた番号に合わせる
        if (response.page !== page) {
          setPage(response.page);
        }
      } catch {
        setError("発送依頼履歴の取得に失敗しました。ページを読み込みなおして再度お試しください。");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [page]);

  const handleSelectPage = (nextPage: number) => {
    if (nextPage === page || nextPage < 1 || nextPage > totalPages) {
      return;
    }
    setPage(nextPage);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error !== "") {
    return <p className="text-danger text-center my-4">{error}</p>;
  }

  if (totalCount === 0) {
    return <p className="fs-description text-center my-4">発送依頼の履歴はまだありません。</p>;
  }

  return (
    <div className="contentContainer px-2">
      <p className="fs-caption mb-3">
        ※発送状況についてのお問い合わせの際は、注文番号をお知らせください。
      </p>
      <p className="fs-caption mb-3">
        全{totalCount}件中 {(page - 1) * PAGE_SIZE + 1}〜{(page - 1) * PAGE_SIZE + histories.length}件を表示しています。
      </p>
      {/* 注文番号は秒精度のため同一秒の依頼で重複しうる。key には使わない */}
      {histories.map((history, index) => (
        <div className="mb-4" key={index}>
          <Table className="m-0" striped bordered hover>
            <tbody className="fs-body align-middle text-center">
              <tr>
                <td className="w-25">注文番号</td>
                <td>{history.order_number}</td>
              </tr>
              <tr>
                <td className="w-25">発送依頼日時</td>
                <td>{history.create}</td>
              </tr>
              <tr>
                <td className="w-25">お届け先</td>
                <td>
                  {history.recipient_name} 様<br />
                  {history.postcode}<br />
                  {history.address}
                </td>
              </tr>
              <tr>
                <td className="w-25">発送商品</td>
                <td className="p-0">
                  {Object.entries(history.deliveryproductvariation_set).map(([productName, productVariations]) => (
                    <React.Fragment key={productName}>
                      {productName} [{productVariations.length}枚]<br />
                      {
                        productVariations.map((variation, idx) => (
                          <React.Fragment key={variation.id + '-' + idx}>
                            {variation.rarity} {variation.name} 裏面画像番号：{variation.back_number || 'チームロゴ'}<br />
                          </React.Fragment>
                        ))
                      }
                    </React.Fragment>
                  ))}
                </td>
              </tr>
              <tr>
                <td className="w-25">依頼枚数合計</td>
                <td className="text-end">{getTotalProductVariations(history.deliveryproductvariation_set)}枚</td>
              </tr>
            </tbody>
          </Table>
        </div>
      ))}
      {totalPages > 1 && (
        <Pagination className="justify-content-center flex-wrap">
          <Pagination.Prev disabled={page <= 1} onClick={() => handleSelectPage(page - 1)} />
          {buildPageNumbers(page, totalPages).map(pageNumber => (
            <Pagination.Item
              key={pageNumber}
              active={pageNumber === page}
              onClick={() => handleSelectPage(pageNumber)}
            >
              {pageNumber}
            </Pagination.Item>
          ))}
          <Pagination.Next disabled={page >= totalPages} onClick={() => handleSelectPage(page + 1)} />
        </Pagination>
      )}
    </div>
  );
};

export default DeliveryHistoryList;
